import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@material-ui/core';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Draggable from 'react-draggable';
import BootstrapTable from 'react-bootstrap-table-next';
import api from '../../services/api';
import colors from '../../styles/settings/colors';

const ProcessFlowModal = ({ 
  open, 
  onClose, 
  antecipacaoId, 
  fornecedorId,
  fornecedorNome 
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && antecipacaoId) {
      fetchProcessFlow();
    }
  }, [open, antecipacaoId]);

  const fetchProcessFlow = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('OperacoesAntecipacoes/ConsultarEsteiraProcessos', {
        AntecipacaoId: antecipacaoId,
        FornecedorId: fornecedorId
      });

      if (response.data.status === 'Sucesso') {
        setData(response.data.resposta?.itens || []);
      } else {
        setError(response.data.complemento?.mensagens?.[0] || 'Erro ao carregar esteira de processos');
      }
    } catch (err) {
      setError('Erro ao carregar esteira de processos');
      console.error('Erro ao buscar esteira de processos:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const columns = [
    {
      dataField: 'data',
      text: 'Data',
      headerStyle: { width: '15%', backgroundColor: colors.sanMarino, color: '#fff', textAlign: 'left' },
      style: { width: '15%', textAlign: 'left' },
      formatter: (cell) => formatDate(cell),
      sort: false
    },
    {
      dataField: 'analista',
      text: 'Operador/Fornecedores/Rede',
      headerStyle: { width: '25%', backgroundColor: colors.sanMarino, color: '#fff', textAlign: 'left' },
      style: { width: '25%', textAlign: 'left' },
      sort: false
    },
    {
      dataField: 'esteira',
      text: 'Status',
      headerStyle: { width: '15%', backgroundColor: colors.sanMarino, color: '#fff', textAlign: 'left' },
      style: { width: '15%', textAlign: 'left' },
      sort: false
    },
    {
      dataField: 'observacoes',
      text: 'Situação',
      headerStyle: { width: '45%', backgroundColor: colors.sanMarino, color: '#fff', textAlign: 'left' },
      style: { width: '45%', textAlign: 'left' },
      formatter: (cell) => cell || '-',
      sort: false
    }
  ];

  return (
    <Draggable handle='.logs-modal-header'>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        hideBackdrop
        PaperProps={{
          style: {
            width: 'auto',
            maxWidth: '90vw',
            margin: '20px',
            padding: '8px'
          }
        }}
      >
        <div style={{ 
          border: `2px solid ${colors.sanMarino}`,
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <div className="logs-modal-header" style={{
            background: colors.sanMarino,
            color: '#fff',
            padding: '8px 24px',
            cursor: 'grab',
            userSelect: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 0
          }}>
            <Typography variant="h6" style={{ color: '#fff', fontWeight: 'bold', margin: 0, textAlign: 'center' }}>
              Esteira de Processos
            </Typography>
          </div>

          <DialogContent dividers={false} style={{ 
            padding: 0, 
            overflow: 'auto', 
            margin: 0,
            maxHeight: '60vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography color="error">{error}</Typography>
            </Box>
          ) : (
            <Box style={{ width: '100%', margin: 0 }}>
              {data && data.length > 0 ? (
                <div style={{ width: '100%', margin: 0 }}>
                  <style>
                    {`
                      .process-flow-table table {
                        width: 100% !important;
                        table-layout: fixed;
                        border-collapse: collapse;
                      }
                      .process-flow-table .table-responsive {
                        width: 100% !important;
                        max-height: 60vh;
                        overflow-y: auto;
                      }
                      .process-flow-table thead th {
                        background-color: ${colors.sanMarino} !important;
                        border-right: 2px solid #fff !important;
                        position: sticky;
                        top: 0;
                        z-index: 10;
                      }
                      .process-flow-table thead th:last-child {
                        border-right: none !important;
                      }
                      .process-flow-table th,
                      .process-flow-table td {
                        word-wrap: break-word;
                        text-align: left !important;
                        padding: 8px 12px;
                      }
                      .process-flow-table tbody tr:nth-child(even) {
                        background-color: #f5f5f5;
                      }
                      .process-flow-table tbody tr:nth-child(odd) {
                        background-color: #ffffff;
                      }
                      .process-flow-table tbody tr:hover {
                        background-color: #e3f2fd !important;
                      }
                    `}
                  </style>
                  <div className="process-flow-table" style={{ width: '100%' }}>
                    <BootstrapTable
                      keyField={(row, rowIndex) => `row-${rowIndex}`}
                      data={data}
                      columns={columns}
                      bootstrap4
                      bordered={false}
                      hover
                      condensed
                      noDataIndication="Nenhum processo encontrado para esta operação"
                      wrapperClasses="table-responsive"
                      classes="table-sm"
                      defaultSorted={[]}
                      sort={false}
                    />
                  </div>
                </div>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <Typography variant="body1" color="textSecondary">
                    Nenhum processo encontrado para esta operação.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          </DialogContent>

          <DialogActions style={{ justifyContent: 'center', padding: '12px 24px', margin: 0, borderTop: `1px solid ${colors.sanMarino}` }}>
            <Button 
              onClick={onClose} 
              color="primary"
              style={{
                background: colors.sanMarino,
                color: '#fff',
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </div>
      </Dialog>
    </Draggable>
  );
};

export default ProcessFlowModal;
